import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import {
  CatalogByCategory,
  getMainCategoryInfo,
  LedPreset,
  MainCategoriesEnum,
  ProductConfig,
  ProductNameEnum,
  ProductUiState,
  TextureCategory,
  UID,
  UIMode,
} from "@shared/core";
import {
  defaultConfigFromCatalog,
  getDefaultDataByProduct,
  keyFor,
  loadProductNode,
  ROOT_KEY,
  saveProductNode,
} from "./localStorage";
import {
  getInitialGroupForCategory,
  getInitialItemForCategory,
} from "../utils/common";

/**
 * Per-product node that we want to store in localStorage:
 * - config: user selections per category
 * - ui: extra UI state for this product
 */
export type ProductStateSlice = {
  config: ProductConfig;
  ui: ProductUiState;
};

/**
 * Root store state.
 */
type StoreState = {
  activeProduct: ProductNameEnum;
  activeMainCategory: MainCategoriesEnum;
  catalog: CatalogByCategory;

  products: Partial<Record<ProductNameEnum, ProductStateSlice>>;

  setActiveProduct: (p: ProductNameEnum) => void;
  setActiveMainCategory: (c: MainCategoriesEnum) => void;
  setSelectedGroup: (
    groupUid: string | null,
    category?: MainCategoriesEnum,
  ) => void;

  selectSingle: (category: MainCategoriesEnum, uid: string) => void;
  toggleMulti: (category: MainCategoriesEnum, uid: string) => void;

  resetActiveProductConfig: () => void;
  resetAllProductsToDefaults: (opts?: {
    resetActiveInMemory?: boolean;
  }) => void;

  setMode: (mode: UIMode) => void;
  setLed: (led: LedPreset) => void;
  setLocale: (locale: string | null) => void;
};

/**
 * Helper: build a fresh product slice from catalog + category.
 * Used when there is no saved node in localStorage.
 */
export function makeInitialProductSlice(
  product: ProductNameEnum,
  catalog: CatalogByCategory,
  category: MainCategoriesEnum,
): ProductStateSlice {
  const config = defaultConfigFromCatalog(catalog);

  if (product === ProductNameEnum.SAY52) {
    config[MainCategoriesEnum.EXTRAS] = {
      kind: "multi",
      uids: ["bi-colour"],
    };
  }

  const selectedGroup: Partial<Record<MainCategoriesEnum, string | null>> = {};
  (Object.keys(catalog) as MainCategoriesEnum[]).forEach((catKey) => {
    selectedGroup[catKey] = getInitialGroupForCategory(catalog[catKey] as any);
  });

  return {
    config,
    ui: {
      product,
      category,
      selectedGroup,
      mode: "day",
      led: "off",
      locale: "en",
    },
  };
}
/**
 * Try to restore last active product/category; fall back to defaults.
 */
function loadRootUi(): {
  product: ProductNameEnum;
  category: MainCategoriesEnum;
} {
  /* @TODO Removed sync-feature */
  // try {
  //   const raw = localStorage.getItem(ROOT_KEY);
  //   if (raw) {
  //     const parsed = JSON.parse(raw) as {
  //       p?: ProductNameEnum;
  //       c?: MainCategoriesEnum;
  //     };
  //     if (parsed?.p && parsed?.c) {
  //       return { product: parsed.p, category: parsed.c };
  //     }
  //   }
  // } catch {}

  return {
    product: ProductNameEnum.SAY52,
    category: MainCategoriesEnum.PROPULSION,
  };
}

const { product: initialProduct, category: initialCategory } = loadRootUi();
const initialCatalog = getDefaultDataByProduct(initialProduct);

/* @TODO Removed sync-feature */
// const loadedNode = loadProductNode(initialProduct);
//
// const initialNode: ProductStateSlice =
//   loadedNode ??
//   makeInitialProductSlice(initialProduct, initialCatalog, MainCategoriesEnum.HULL);

const initialNode: ProductStateSlice = makeInitialProductSlice(
  initialProduct,
  initialCatalog,
  MainCategoriesEnum.PROPULSION,
);

let isApplyingRemoteUpdate = false;
/**
 * Main config store.
 */
export const useConfigStore = create<StoreState>()(
  subscribeWithSelector((set, get) => ({
    activeProduct: initialProduct,
    activeMainCategory: initialNode.ui.category,
    catalog: initialCatalog,
    products: {
      [initialProduct]: initialNode,
    },

    /**
     * Switch active product.
     * Loads product node from localStorage if present, otherwise creates a fresh slice.
     */
    setActiveProduct: (p) => {
      const current = get();
      if (current.activeProduct === p) return;

      const catalog = getDefaultDataByProduct(p);

      const existing = current.products[p];

      // Do not read localStorage; only use in-memory state or a fresh slice
      const slice =
        existing ??
        makeInitialProductSlice(p, catalog, current.activeMainCategory);

      const hasCategory = slice.ui.category in catalog;
      const safeCategory = hasCategory
        ? slice.ui.category
        : ((Object.keys(catalog)[0] as MainCategoriesEnum) ??
          MainCategoriesEnum.PROPULSION);

      slice.ui.category = safeCategory;

      set((s) => ({
        activeProduct: p,
        activeMainCategory: safeCategory,
        catalog,
        products: {
          ...s.products,
          [p]: slice,
        },
      }));
    },
    /* @TODO Removed sync-feature */
    // setActiveProduct: (p) => {
    //   const current = get();
    //   if (current.activeProduct === p) return;
    //
    //   const catalog = getDefaultDataByProduct(p);
    //
    //   const existing = current.products[p];
    //   const fromStorage = loadProductNode(p);
    //   const slice =
    //     existing ??
    //     fromStorage ??
    //     makeInitialProductSlice(
    //       p,
    //       catalog,
    //       current.activeMainCategory
    //     );
    //
    //
    //   const hasCategory = slice.ui.category in catalog;
    //   const safeCategory = hasCategory
    //     ? slice.ui.category
    //     : ((Object.keys(catalog)[0] as MainCategoriesEnum) ??
    //       MainCategoriesEnum.PROPULSION);
    //
    //   slice.ui.category = safeCategory;
    //
    //   set((s) => ({
    //     activeProduct: p,
    //     activeMainCategory: safeCategory,
    //     catalog,
    //     products: {
    //       ...s.products,
    //       [p]: slice,
    //     },
    //   }));
    // },

    /**
     * Change active main category for the current product.
     * Also updates the per-product ui.category.
     */
    setActiveMainCategory: (category) => {
      const { activeProduct, activeMainCategory } = get();

      // Guard: avoid useless updates
      if (category === activeMainCategory) return;

      set((s) => {
        const catalog: CatalogByCategory = s.catalog;
        const prevNode =
          s.products[activeProduct] ??
          makeInitialProductSlice(activeProduct, catalog, category);

        const cat = catalog[category];

        let nextConfig = prevNode.config;
        let nextUi = { ...prevNode.ui, category };

        if (cat && cat.type === "texture") {
          const texCat = cat as TextureCategory;

          const prevSelectedGroups = prevNode.ui.selectedGroup ?? {};
          const existingGroup = prevSelectedGroups[category] ?? null;

          let groupUid = existingGroup;

          if (!groupUid) {
            groupUid = getInitialGroupForCategory(texCat) ?? null;
          }

          if (groupUid) {
            if (groupUid !== existingGroup) {
              nextUi = {
                ...nextUi,
                selectedGroup: {
                  ...prevSelectedGroups,
                  [category]: groupUid,
                },
              };
            }

            const rawSelection = prevNode.config?.[category];

            if (!rawSelection) {
              const initialItem =
                getInitialItemForCategory(texCat) ?? texCat.items[0];

              if (initialItem) {
                nextConfig = {
                  ...nextConfig,
                  /* @ts-ignore */
                  [category]: { kind: "single", uid: initialItem.uid as UID },
                };
              }
            }
          }
        }

        const nextNode: ProductStateSlice = {
          ...prevNode,
          config: nextConfig,
          ui: nextUi,
        };

        return {
          activeMainCategory: category,
          products: {
            ...s.products,
            [activeProduct]: nextNode,
          },
        };
      });
    },

    /**
     * Single-selection update for a category.
     */
    selectSingle: (category, uid) => {
      const { activeProduct } = get();
      set((s) => {
        const prevNode =
          s.products[activeProduct] ??
          makeInitialProductSlice(
            activeProduct,
            s.catalog,
            s.activeMainCategory,
          );

        const priorConfig = prevNode.config ?? ({} as ProductConfig);
        const nextConfig: ProductConfig = {
          ...priorConfig,
          [category]: { kind: "single", uid },
        };

        const nextNode: ProductStateSlice = {
          ...prevNode,
          config: nextConfig,
        };

        return {
          products: {
            ...s.products,
            [activeProduct]: nextNode,
          },
        };
      });
    },

    /**
     * Multi-selection toggle for a category.
     */
    toggleMulti: (category, uid) => {
      const { activeProduct } = get();
      if (
        activeProduct === ProductNameEnum.SAY52 &&
        category === MainCategoriesEnum.EXTRAS &&
        uid === "bi-colour"
      ) {
        return;
      }
      set((s) => {
        const prevNode =
          s.products[activeProduct] ??
          makeInitialProductSlice(
            activeProduct,
            s.catalog,
            s.activeMainCategory,
          );

        const priorConfig = prevNode.config ?? ({} as ProductConfig);
        const prevCat = priorConfig[category];

        let uids: string[] = [];
        if (prevCat?.kind === "multi") {
          const has = prevCat.uids.includes(uid);
          uids = has
            ? prevCat.uids.filter((x) => x !== uid)
            : [...prevCat.uids, uid];
        } else {
          uids = [uid];
        }

        const nextConfig: ProductConfig = {
          ...priorConfig,
          [category]: { kind: "multi", uids },
        };

        const nextNode: ProductStateSlice = {
          ...prevNode,
          config: nextConfig,
        };

        return {
          products: {
            ...s.products,
            [activeProduct]: nextNode,
          },
        };
      });
    },

    // setSelectedGroup: (category, groupUid) => {
    //   const { activeProduct } = get();
    //
    //   set((s) => {
    //     const prev = s.products[activeProduct];
    //     if (!prev) return s;
    //
    //     const prevSel = prev.ui.selectedGroup ?? {};
    //
    //     return {
    //       products: {
    //         ...s.products,
    //         [activeProduct]: {
    //           ...prev,
    //           ui: {
    //             ...prev.ui,
    //             selectedGroup: {
    //               ...prevSel,
    //               [category as string]: groupUid,
    //             },
    //           },
    //         },
    //       },
    //     };
    //   });
    // },

    setSelectedGroup: (category, groupUid) => {
      const { activeProduct } = get();

      set((s) => {
        const prevNode = s.products[activeProduct];
        if (!prevNode) return s;

        const catalog: CatalogByCategory = s.catalog;
        /* @ts-ignore */
        const cat = catalog[category] as TextureCategory | undefined;

        const prevSel = prevNode.ui.selectedGroup ?? {};
        let nextConfig = prevNode.config;

        // 1) оновлюємо selectedGroup в ui
        const nextSelectedGroup = {
          ...prevSel,
          [category as string]: groupUid,
        };

        if (cat && cat.type === "texture" && groupUid) {
          const groupItem =
            cat.items.find((i) => i.group === groupUid) ?? cat.items[0];

          if (groupItem) {
            nextConfig = {
              ...nextConfig,
              [category as string]: {
                kind: "single",
                uid: groupItem.uid as UID,
              },
            };
          }
        }

        return {
          products: {
            ...s.products,
            [activeProduct]: {
              ...prevNode,
              config: nextConfig,
              ui: {
                ...prevNode.ui,
                selectedGroup: nextSelectedGroup,
              },
            },
          },
        };
      });
    },

    setMode: (mode) => {
      const { activeProduct } = get();
      set((s) => {
        const prev =
          s.products[activeProduct] ??
          makeInitialProductSlice(
            activeProduct,
            s.catalog,
            s.activeMainCategory,
          );

        return {
          products: {
            ...s.products,
            [activeProduct]: {
              ...prev,
              ui: {
                ...prev.ui,
                mode,
              },
            },
          },
        };
      });
    },

    setLed: (led) => {
      const { activeProduct } = get();
      set((s) => {
        const prev =
          s.products[activeProduct] ??
          makeInitialProductSlice(
            activeProduct,
            s.catalog,
            s.activeMainCategory,
          );

        return {
          products: {
            ...s.products,
            [activeProduct]: {
              ...prev,
              ui: {
                ...prev.ui,
                led,
              },
            },
          },
        };
      });
    },

    setLocale: (locale) => {
      const { activeProduct } = get();
      set((s) => {
        const prev =
          s.products[activeProduct] ??
          makeInitialProductSlice(
            activeProduct,
            s.catalog,
            s.activeMainCategory,
          );

        return {
          products: {
            ...s.products,
            [activeProduct]: {
              ...prev,
              ui: {
                ...prev.ui,
                locale,
              },
            },
          },
        };
      });
    },

    /**
     * Reset current product config to defaults (but keep its UI slice).
     */
    resetActiveProductConfig: () => {
      const { activeProduct } = get();
      const catalog = getDefaultDataByProduct(activeProduct);
      const defConfig = defaultConfigFromCatalog(catalog);

      set((s) => {
        const prevNode =
          s.products[activeProduct] ??
          makeInitialProductSlice(activeProduct, catalog, s.activeMainCategory);

        const nextNode: ProductStateSlice = {
          ...prevNode,
          config: defConfig,
        };

        return {
          products: {
            ...s.products,
            [activeProduct]: nextNode,
          },
        };
      });
    },

    resetAllProductsToDefaults: (opts) => {
      const { resetActiveInMemory = true } = opts ?? {};

      // Rewrite localStorage entries for every product with default slices
      (Object.values(ProductNameEnum) as ProductNameEnum[]).forEach(
        (product) => {
          const catalog = getDefaultDataByProduct(product);

          // Use a stable default category for initialization
          const node = makeInitialProductSlice(
            product,
            catalog,
            MainCategoriesEnum.PROPULSION,
          );

          // Overwrite existing value; no need to remove first
          saveProductNode(product, node);
        },
      );

      if (!resetActiveInMemory) return;

      // Also reset current in-memory state so UI updates immediately
      const { activeProduct } = get();
      const catalog = getDefaultDataByProduct(activeProduct);
      const node = makeInitialProductSlice(
        activeProduct,
        catalog,
        MainCategoriesEnum.PROPULSION,
      );

      set((s) => ({
        ...s,
        catalog,
        activeMainCategory: node.ui.category,
        products: {
          ...s.products,
          [activeProduct]: node,
        },
      }));
    },
  })),
);

/**
 * Persist the whole product node for the active product on each change.
 */
/* @TODO Removed sync-feature */
// useConfigStore.subscribe(
//   (s) => {
//     const p = s.activeProduct;
//     return [p, s.products[p]] as const;
//   },
//   ([product, productNode]) => {
//     if (!product || !productNode) return;
//     /* prevent multi-tab recursion */
//     if (isApplyingRemoteUpdate) return;
//     saveProductNode(product, productNode);
//   }
// );
useConfigStore.subscribe(
  (s) => {
    const p = s.activeProduct;
    return [p, s.products[p]] as const;
  },
  ([product, productNode]) => {
    if (!product || !productNode) return;
    if (isApplyingRemoteUpdate) return;
    saveProductNode(product, productNode);
  },
);

/**
 * Optionally keep last active product/category for root UI.
 */
/* @TODO Removed sync-feature */
// useConfigStore.subscribe(
//   (s) => ({ p: s.activeProduct, c: s.activeMainCategory }),
//   (root) => {
//     try {
//       localStorage.setItem(ROOT_KEY, JSON.stringify(root));
//     } catch {
//     }
//   }
// );
useConfigStore.subscribe(
  (s) => ({ p: s.activeProduct, c: s.activeMainCategory }),
  (root) => {
    try {
      localStorage.setItem(ROOT_KEY, JSON.stringify(root));
    } catch {}
  },
);

/* @TODO Removed sync-feature */
// window.addEventListener("storage", (e) => {
//   if (!e.key || !e.newValue) return;
//
//   try {
//     const { activeProduct } = useConfigStore.getState();
//     if (e.key === keyFor(activeProduct)) {
//       const node = JSON.parse(e.newValue) as ProductStateSlice;
//       isApplyingRemoteUpdate = true;
//       useConfigStore.setState((s) => ({
//         products: { ...s.products, [activeProduct]: node },
//         activeMainCategory: node.ui.category,
//       }));
//       isApplyingRemoteUpdate = false;
//     }
//   } catch {
//   }
// });
